import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

export class FetchError extends Error {
  status: number
  constructor(status: number, message?: string) {
    super(message ?? `HTTP ${status}`)
    this.status = status
  }
}

interface FetchOptions {
  headers?: Record<string, string>
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
}

interface RequestOptions {
  silent?: boolean
}

export const useFetch = (url: string, _options?: FetchOptions) => {
  const notificationStore = useNotificationStore()
  const baseUrl = import.meta.env.VITE_VENTURE360_API_URL.replace('/v3', '')
  const authStore = useAuthStore()
  const baseOptions: RequestInit = {
    headers: {
      Authorization: `Bearer ${authStore.access_token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  }

  const get = async <T>(opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const res = await fetch(`${baseUrl}${url}`, baseOptions)

    if (!res.ok) {
      if (res.status === 401) throw new Error('Your session has expired. Please refresh and try again.')
      let message = 'An unknown error occurred'
      try {
        const data: { error?: string | string[]; message?: string } = await res.json()
        message = data.message || (Array.isArray(data.error) ? data.error[0] : data.error) || message
      } catch {
        console.error(`${res.status} ${res.statusText}`)
      }
      if (!silent) notificationStore.enqueue('error', message)
      throw new FetchError(res.status, message)
    }

    return (await res.json()) as T
  }

  const download = async <T>(opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const baseOptionsL = {
      headers: {
        Authorization: `Bearer ${authStore.access_token}`,
      },
    }
    const res = await fetch(`${baseUrl}${url}`, baseOptionsL)

    if (!res.ok) {
      if (res.status === 401) throw new Error('Your session has expired. Please refresh and try again.')
      let message = 'Failed to download file'
      try {
        const text = await res.text()
        console.error('Error response from server:', text)
        try {
          const data: { error?: string | string[]; message?: string } = JSON.parse(text)
          message = data.message || (Array.isArray(data.error) ? data.error[0] : data.error) || message
        } catch {
          // Response body wasn't JSON; keep the default message
        }
      } catch {
        // Couldn't read the response body at all
      }
      if (!silent) notificationStore.enqueue('error', message)
      throw new FetchError(res.status, message)
    }

    return res as T
  }

  const buildHeaders = (isFormData: boolean): HeadersInit => {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${authStore.access_token}`,
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }
    return headers
  }

  function toPayload(data?: unknown): BodyInit | undefined {
    if (!data) return undefined
    if (data instanceof FormData) return data
    return JSON.stringify(data)
  }

  const post = async <T>(body?: unknown, opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const isFormData = body instanceof FormData
    const headers = buildHeaders(isFormData)
    const payload = toPayload(body)

    const res = await fetch(`${baseUrl}${url}`, {
      ...baseOptions,
      method: 'POST',
      headers,
      body: payload,
    })

    if (!res.ok) {
      if (res.status === 401) throw new Error('Your session has expired. Please refresh and try again.')
      let message = 'An unknown error occurred'
      try {
        const data: { error?: string | string[]; message?: string } = await res.json()
        message = data.message || (Array.isArray(data.error) ? data.error[0] : data.error) || message
      } catch {
        // empty or malformed response body
      }
      if (!silent) notificationStore.enqueue('error', message)
      throw new FetchError(res.status, message)
    }

    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as T
    }

    const text = await res.text()
    return text ? (JSON.parse(text) as T) : (undefined as T)
  }

  const del = async <T>(body?: unknown, opts?: RequestOptions): Promise<T> => {
    const { silent = true } = opts ?? {}
    const isFormData = body instanceof FormData
    const headers = buildHeaders(isFormData)
    const payload = toPayload(body)

    const res = await fetch(`${baseUrl}${url}`, {
      ...baseOptions,
      method: 'DELETE',
      headers,
      body: payload,
    })

    if (!res.ok) {
      if (res.status === 401) throw new Error('Your session has expired. Please refresh and try again.')
      let message = 'An unknown error occurred'
      try {
        const data: { error?: string | string[]; message?: string } = await res.json()
        message = data.message || (Array.isArray(data.error) ? data.error[0] : data.error) || message
      } catch {
        message = res.statusText || message
      }
      if (!silent) notificationStore.enqueue('error', message)
      throw new FetchError(res.status, message)
    }

    return res.status === 204 ? undefined : ((await res.json()) as T)
  }

  return { get, post, download, delete: del }
}
